/*
 * MANUALREVIEWHOURSMODAL.JSX - Focused Manual Review Modal For Pending Timesheets
 *
 * SUMMARY:
 * - Provides a constrained review flow for records that require manual intervention.
 * - Lets reviewers rebalance Normal/OT1/OT2 hours while preserving the original total duration.
 */

import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/modal/modal";
import CustomInput from "../../../components/inputs/new-input";

const ROUNDING_EPSILON = 0.01;

/**
 * Converts a value to a number and falls back to 0.
 *
 * @param {string|number} value - Candidate numeric value.
 * @returns {number} Numeric value.
 */
const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Formats a numeric value to two decimal places for form fields.
 *
 * @param {number|string} value - Value to format.
 * @returns {string} Two-decimal formatted value.
 */
const toTwoDecimals = (value) => toNumber(value).toFixed(2);

const ManualReviewHoursModal = ({ open, allocation, onClose, onSubmit }) => {
    const [normalHours, setNormalHours] = useState("0.00");
    const [overtime1, setOvertime1] = useState("0.00");
    const [overtime2, setOvertime2] = useState("0.00");
    const [errorMessage, setErrorMessage] = useState("");

    const originalDuration = useMemo(() => {
        if (!allocation?.startTime || !allocation?.endTime) return 0;
        const start = new Date(allocation.startTime);
        const end = new Date(allocation.endTime);
        const durationInHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return Number.isFinite(durationInHours) && durationInHours > 0 ? durationInHours : 0;
    }, [allocation]);

    const allowedNormalHours = useMemo(() => {
        const value = toNumber(allocation?.globalHrs);
        return Number.isFinite(value) && value > 0 ? value : 0;
    }, [allocation]);

    useEffect(() => {
        if (!open || !allocation) return;
        const currentOt1 = toNumber(allocation.overtime_1);
        const currentOt2 = toNumber(allocation.overtime_2);
        const computedNormal = Math.max(originalDuration - (currentOt1 + currentOt2), 0);

        setNormalHours(toTwoDecimals(computedNormal));
        setOvertime1(toTwoDecimals(currentOt1));
        setOvertime2(toTwoDecimals(currentOt2));
        setErrorMessage("");
    }, [open, allocation, originalDuration]);

    /**
     * Validates modal values before saving.
     *
     * Rules:
     * - No negative values.
     * - Normal hours must stay within configured daily normal-hours limit.
     * - Total edited hours must exactly match original duration (within epsilon).
     *
     * @returns {boolean} True when all checks pass.
     */
    const validateBeforeSubmit = () => {
        const normal = toNumber(normalHours);
        const ot1 = toNumber(overtime1);
        const ot2 = toNumber(overtime2);

        if (normal < 0 || ot1 < 0 || ot2 < 0) {
            setErrorMessage("Hours cannot be negative.");
            return false;
        }

        if (allowedNormalHours > 0 && normal - allowedNormalHours > ROUNDING_EPSILON) {
            setErrorMessage(`Normal hours must be ${allowedNormalHours.toFixed(2)}h or less.`);
            return false;
        }

        const totalEdited = normal + ot1 + ot2;
        if (Math.abs(totalEdited - originalDuration) > ROUNDING_EPSILON) {
            setErrorMessage(
                `Total hours must remain ${originalDuration.toFixed(2)}h. Current total is ${totalEdited.toFixed(2)}h.`
            );
            return false;
        }

        setErrorMessage("");
        return true;
    };

    const handleSave = () => {
        if (!validateBeforeSubmit()) return;
        onSubmit({
            overtime1: toNumber(overtime1),
            overtime2: toNumber(overtime2),
            normalHours: toNumber(normalHours),
            originalDuration,
        });
    };

    return (
        <Modal
            fullWidth={true}
            maxWidth="sm"
            save={<span data-cy="manual-review-save">Save and Approve</span>}
            cancel="Cancel"
            footerBtns={true}
            onClick={onClose}
            onEdit={handleSave}
            open={open}
            onClose={onClose}
            disableBackdropClick={true}
            modalHeading="Manual Review Required"
        >
            <div className="manual-review-hours-modal" data-cy="manual-review-modal">
                <p className="manual-review-hours-modal__hint" data-cy="manual-review-hint">
                    Keep total at <b>{originalDuration.toFixed(2)}h</b>. Normal hours must be{" "}
                    <b>{allowedNormalHours.toFixed(2)}h</b> or less.
                </p>

                <div className="row mb-2">
                    <div className="col-4">
                        <CustomInput
                            name="normalHours"
                            type="number"
                            label="Regular Hours"
                            value={normalHours}
                            formatDecimals={true}
                            customInputClass="text-right"
                            onChange={(e) => setNormalHours(e.target.value)}
                            onBlur={() => setNormalHours(toTwoDecimals(normalHours))}
                            dataCy="manual-review-normal-hours"
                        />
                    </div>
                    <div className="col-4">
                        <CustomInput
                            name="overtime_1"
                            type="number"
                            label="Overtime one"
                            value={overtime1}
                            formatDecimals={true}
                            customInputClass="text-right"
                            onChange={(e) => setOvertime1(e.target.value)}
                            onBlur={() => setOvertime1(toTwoDecimals(overtime1))}
                            dataCy="manual-review-overtime-1"
                        />
                    </div>
                    <div className="col-4">
                        <CustomInput
                            name="overtime_2"
                            type="number"
                            label="Overtime two"
                            value={overtime2}
                            formatDecimals={true}
                            customInputClass="text-right"
                            onChange={(e) => setOvertime2(e.target.value)}
                            onBlur={() => setOvertime2(toTwoDecimals(overtime2))}
                            dataCy="manual-review-overtime-2"
                        />
                    </div>
                </div>

                {errorMessage && (
                    <div className="manual-review-hours-modal__error" data-cy="manual-review-error">
                        {errorMessage}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ManualReviewHoursModal;
